import React, { useState, useContext, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { certificatesAPI } from "../../lib/apiService";
import { APIRequestContext } from "../../context";
import "./CertificateVerification.css";

// LinkedIn and Download icons (you can replace these with actual icons from an icon library)
const LinkedInIcon = () => (
  <svg className="action-icon" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
  </svg>
);

const DownloadIcon = () => (
  <svg className="action-icon" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
  </svg>
);

function CertificateVerification() {
  const [certificateId, setCertificateId] = useState("");
  const [verificationResult, setVerificationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageObjectUrl, setImageObjectUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const { request_handler } = useContext(APIRequestContext);

  // Function to fetch and create image object URL
  const fetchAndCreateImageUrl = async (url) => {
    if (!url) return;
    
    setLoading(true);
    setImageError(null);
    
    try {
      // Prefer preview variant if URL points to a full-res certificate
      let urlToFetch = url;
      try {
        const parsed = new URL(url);
        if (parsed.pathname.includes('/uploads/certificates/')) {
          const filename = parsed.pathname.split('/').pop() || '';
          const certificateNumber = (verificationResult?.data?.certificateNumber) || filename
            .replace(/^cert-/i, '')
            .replace(/-preview/i, '')
            .replace(/\.(png|jpg|jpeg|webp)$/i, '');
          const origin = `${parsed.protocol}//${parsed.hostname}`; // avoid port/path corruption
          const candidatePreview = `${origin}/uploads/certificates/cert-${certificateNumber}-preview.webp`;
          urlToFetch = candidatePreview;
        }
      } catch (_) {
        // ignore parsing errors; use original URL
      }

      console.log('Attempting to fetch certificate image from:', urlToFetch);
      
      const response = await fetch(urlToFetch, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Authorization': localStorage.getItem("access_token") 
            ? `Bearer ${localStorage.getItem("access_token")}` 
            : null,
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) {
        throw new Error(`Received non-image content: ${blob.type}`);
      }
      const objectUrl = URL.createObjectURL(blob);
      setImageObjectUrl(objectUrl);
      console.log('Successfully loaded certificate image');
    } catch (err) {
      console.error('Error fetching certificate image:', err);
      setImageError(`Unable to load certificate image: ${err.message}`);
      
      // Try fallback download URL from backend if available
      if (verificationResult?.data?.certificateDownloadUrl) {
        try {
          const altResponse = await fetch(verificationResult.data.certificateDownloadUrl, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
              'Authorization': localStorage.getItem("access_token") 
                ? `Bearer ${localStorage.getItem("access_token")}` 
                : null,
            }
          });
          
          if (altResponse.ok) {
            const altBlob = await altResponse.blob();
            if (altBlob.type.startsWith('image/')) {
              const altObjectUrl = URL.createObjectURL(altBlob);
              setImageObjectUrl(altObjectUrl);
              setImageError(null);
              console.log('Successfully loaded certificate image via download URL');
              return;
            }
          }
        } catch (altErr) {
          console.error('Fallback URL also failed:', altErr);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Get parameters from URL
  const { certificateId: paramCertificateId } = useParams();
  const location = useLocation();
  
  // Cleanup function for image object URL
  useEffect(() => {
    return () => {
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
    };
  }, [imageObjectUrl]);

  // Fetch image when verification result changes
  useEffect(() => {
    if (verificationResult?.success && verificationResult?.data?.certificate_url) {
      fetchAndCreateImageUrl(verificationResult.data.certificate_url);
    }
  }, [verificationResult]);

  // Extract certificate ID from URL query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryId = searchParams.get('id');
    
    if (paramCertificateId) {
      // From path parameter (/verify/:certificateId)
      setCertificateId(paramCertificateId);
      verifyAutomatically(paramCertificateId);
    } else if (queryId) {
      // From query parameter (/verify?id=xxx)
      setCertificateId(queryId);
      verifyAutomatically(queryId);
    }
  }, [paramCertificateId, location.search]);
  
  const verifyAutomatically = async (id) => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      console.log('Verifying certificate ID:', id);
      const response = await certificatesAPI.verify(id);
      console.log('Verification response:', response);
      setVerificationResult(response);
    } catch (error) {
      console.error('Verification error:', error);
      console.error('Error response:', error.response);
      setVerificationResult({ 
        success: false, 
        message: error.response?.data?.message || error.message || "Certificate not found or invalid" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!certificateId.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Verifying certificate ID:', certificateId);
      const response = await certificatesAPI.verify(certificateId);
      console.log('Verification response:', response);
      setVerificationResult(response);
    } catch (error) {
      console.error('Verification error:', error);
      console.error('Error response:', error.response);
      setVerificationResult({ 
        success: false, 
        message: error.response?.data?.message || error.message || "Certificate not found or invalid" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCertificateId("");
    setVerificationResult(null);
  };

  return (
    <div className="verification-container">
      <div className="verification-form">
        <div className="verify-hero">
          <div className="brand">
            {/* Place NPLCoder.png inside certification_system_frontend/public */}
            <img src="/NPLCoder.png" alt="NPLCoder" className="brand-logo" />
            <span className="brand-text">NPLCoder</span>
          </div>
          <p className="subtitle">Verification Portal</p>
        </div>

        <form onSubmit={handleVerify}>
          <div className="form-group">
            <label htmlFor="certificateId">Certificate ID</label>
            <input
              type="text"
              id="certificateId"
              value={certificateId}
              onChange={(e) => setCertificateId(e.target.value)}
              placeholder="Enter certificate ID"
              className="form-control"
              required
            />
            <small className="help-text">You can find your certificate ID in the verification email or on your certificate.</small>
          </div>
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify Certificate"}
            </button>
            {verificationResult && (
              <button 
                type="button" 
                className="btn btn-secondary ml-2"
                onClick={handleReset}
              >
                Reset
              </button>
            )}
          </div>
        </form>

        {verificationResult && (
          <div className={`verification-result ${verificationResult.success ? 'success' : 'error'}`}>
            <p className={verificationResult.success ? 'text-success' : 'text-danger'}>
              {verificationResult.message}
            </p>
            
            {verificationResult.success && verificationResult.data && (
              <div className="certificate-details">
                <h4>Certificate Details</h4>
                
                {verificationResult.data.certificate_url && (
                  <div className="certificate-preview">
                    {loading && (
                      <div className="loading-message">
                        <p>Loading certificate...</p>
                      </div>
                    )}
                    
                    {imageError && !loading && (
                      <div className="error-container">
                        <p>Certificate image could not be loaded</p>
                        <p>{imageError}</p>
                        <p>Certificate Number: <strong>{verificationResult.data.certificateNumber}</strong></p>
                        {verificationResult.data.certificateDownloadUrl && (
                          <a href={verificationResult.data.certificateDownloadUrl} target="_blank" rel="noopener noreferrer">
                            Download Certificate (opens in new tab)
                          </a>
                        )}
                      </div>
                    )}
                    
                    {imageObjectUrl && !loading && !imageError && (
                      <>
                        <img 
                          src={imageObjectUrl} 
                          alt="Certificate" 
                          className="certificate-image"
                        />
                        <div className="certificate-actions">
                          <button
                            className="btn btn-linkedin"
                            onClick={() => {
                              // Prepare sharing content
                              const shareUrl = window.location.href;
                              const title = `My ${verificationResult.data.event} Certificate from ${verificationResult.data.category}`;
                              const description = `I received a certificate for ${verificationResult.data.event} in ${verificationResult.data.category}.`;
                              
                              // Open LinkedIn sharing dialog
                              window.open(
                                `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(description)}`,
                                'linkedin-share',
                                'width=600,height=600'
                              );
                            }}
                          >
                            <LinkedInIcon /> Share on LinkedIn
                          </button>
                          <button
                            className="btn btn-download"
                            onClick={async () => {
                              try {
                                // Prefer server download endpoint
                                const downloadUrl = verificationResult.data.certificateDownloadUrl || verificationResult.data.certificate_url;
                                if (!downloadUrl) throw new Error('No download URL available');

                                // Fetch without Authorization header (public endpoint) to avoid CORS preflight
                                const response = await fetch(downloadUrl, {
                                  method: 'GET',
                                  mode: 'cors',
                                  cache: 'no-cache',
                                });
                                if (!response.ok) throw new Error('Download failed');

                                const blob = await response.blob();
                                // Determine file extension from mime type
                                const mime = blob.type || '';
                                let ext = 'png';
                                if (mime.includes('jpeg') || mime.includes('jpg')) ext = 'jpg';
                                else if (mime.includes('webp')) ext = 'webp';
                                else if (mime.includes('pdf')) ext = 'pdf';

                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `certificate-${verificationResult.data.certificateNumber}.${ext}`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } catch (error) {
                                console.error('Download error:', error);
                                // As a fallback, open the download URL in a new tab
                                const fallback = verificationResult?.data?.certificateDownloadUrl || verificationResult?.data?.certificate_url;
                                if (fallback) {
                                  window.open(fallback, '_blank');
                                } else {
                                  alert('Failed to download certificate. Please try again.');
                                }
                              }
                            }}
                          >
                            <DownloadIcon /> Download Certificate
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="certificate-details">
                <p><strong>Name:</strong> {verificationResult.data.name}</p>
                <p><strong>Email:</strong> {verificationResult.data.email}</p>
                <p><strong>Event:</strong> {verificationResult.data.event}</p>
                <p><strong>Category:</strong> {verificationResult.data.category}</p>
                <p><strong>Issue Date:</strong> {new Date(verificationResult.data.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CertificateVerification;
